export const STORAGE_QUOTA_FEATURE_ID = 'storageQuotaMb';
export const MAX_UPLOAD_FILE_FEATURE_ID = 'maxUploadFileMb';

const LEGACY_STORAGE_FEATURE_IDS = new Set(['storage']);
const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

const toFiniteNumber = (value) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
};

export const isLegacyStorageFeature = (featureId) => LEGACY_STORAGE_FEATURE_IDS.has(featureId);

export const normalizeMegabytes = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = toFiniteNumber(value);
  if (numericValue === null || numericValue < 0) {
    return null;
  }
  return numericValue;
};

export const megabytesToBytes = (value) => {
  const normalizedMegabytes = normalizeMegabytes(value);
  if (normalizedMegabytes === null) {
    return null;
  }
  return Math.round(normalizedMegabytes * 1024 * 1024);
};

export const formatBytes = (bytes) => {
  const numericBytes = toFiniteNumber(bytes);
  if (numericBytes === null || numericBytes <= 0) {
    return '0 B';
  }

  let value = numericBytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < BYTE_UNITS.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const roundedValue = value >= 10 || unitIndex === 0
    ? Math.round(value)
    : Math.round(value * 10) / 10;

  return `${roundedValue} ${BYTE_UNITS[unitIndex]}`;
};

export const resolveStorageLimits = (permissions = {}) => {
  const quotaMb = normalizeMegabytes(permissions?.[STORAGE_QUOTA_FEATURE_ID]);
  const maxUploadMb = normalizeMegabytes(permissions?.[MAX_UPLOAD_FILE_FEATURE_ID]);

  return {
    quotaMb,
    quotaBytes: megabytesToBytes(quotaMb),
    maxUploadMb,
    maxUploadBytes: megabytesToBytes(maxUploadMb),
  };
};

export const summarizeStorageAddons = (addons = []) => {
  const activeAddons = (addons || []).filter((addon) => {
    if (!addon || addon.status === 'cancelled') return false;
    if (!addon.expires_at && !addon.expiresAt) return true;
    const expiresAt = new Date(addon.expires_at || addon.expiresAt);
    return Number.isFinite(expiresAt.getTime()) && expiresAt > new Date();
  });

  const extraQuotaMb = activeAddons.reduce((sum, addon) => (
    sum + (normalizeMegabytes(addon.extra_quota_mb ?? addon.extraQuotaMb) || 0)
  ), 0);
  const uploadLimitsMb = activeAddons
    .map((addon) => normalizeMegabytes(addon.max_upload_file_mb ?? addon.maxUploadFileMb))
    .filter((value) => value !== null);
  const maxUploadMb = uploadLimitsMb.length > 0 ? Math.max(...uploadLimitsMb) : null;

  return {
    activeAddons,
    extraQuotaMb,
    extraQuotaBytes: megabytesToBytes(extraQuotaMb) || 0,
    maxUploadMb,
    maxUploadBytes: megabytesToBytes(maxUploadMb),
  };
};

export const applyStorageAddonLimits = (baseLimits = {}, addons = []) => {
  const addonSummary = summarizeStorageAddons(addons);

  return {
    ...baseLimits,
    addonSummary,
    quotaMb: baseLimits.quotaMb === null ? null : (baseLimits.quotaMb || 0) + addonSummary.extraQuotaMb,
    quotaBytes: baseLimits.quotaBytes === null ? null : (baseLimits.quotaBytes || 0) + addonSummary.extraQuotaBytes,
    maxUploadMb: baseLimits.maxUploadMb === null
      ? null
      : addonSummary.maxUploadMb === null
      ? baseLimits.maxUploadMb
      : Math.max(baseLimits.maxUploadMb || 0, addonSummary.maxUploadMb),
    maxUploadBytes: baseLimits.maxUploadBytes === null
      ? null
      : addonSummary.maxUploadBytes === null
      ? baseLimits.maxUploadBytes
      : Math.max(baseLimits.maxUploadBytes || 0, addonSummary.maxUploadBytes),
  };
};

export const calculateStorageUsageMetrics = ({
  usedBytes = 0,
  reservedBytes = 0,
  quotaBytes = null,
} = {}) => {
  const normalizedUsedBytes = Math.max(0, toFiniteNumber(usedBytes) || 0);
  const normalizedReservedBytes = Math.max(0, toFiniteNumber(reservedBytes) || 0);
  const totalBytes = normalizedUsedBytes + normalizedReservedBytes;
  const normalizedQuotaBytes = toFiniteNumber(quotaBytes);
  const hasQuota = normalizedQuotaBytes !== null && normalizedQuotaBytes >= 0;

  return {
    usedBytes: normalizedUsedBytes,
    reservedBytes: normalizedReservedBytes,
    totalBytes,
    quotaBytes: hasQuota ? normalizedQuotaBytes : null,
    availableBytes: hasQuota ? Math.max(normalizedQuotaBytes - totalBytes, 0) : null,
    usedPercentage: hasQuota ? Math.min((totalBytes / normalizedQuotaBytes) * 100, 100) : 0,
  };
};

export const formatPlanPermissionValue = (featureOrId, value) => {
  const featureId = typeof featureOrId === 'string' ? featureOrId : featureOrId?.id;

  if (
    featureId === STORAGE_QUOTA_FEATURE_ID
    || featureId === MAX_UPLOAD_FILE_FEATURE_ID
  ) {
    const bytes = megabytesToBytes(value);
    return bytes === null ? 'Unlimited' : formatBytes(bytes);
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'number' && value >= 999999) {
    return 'Unlimited';
  }

  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  return value;
};
